'use client';

import { useEffect, useState } from 'react';
import { z } from 'zod';

import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { getListSearchResultsQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';
import { trackError } from '@sola/tracking/components/datalayer.js';

const SearchResultsSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().nonnegative().catch(0).optional(),
});

type SearchResultsSchema = z.infer<typeof SearchResultsSchema>;

export const parseUrlParams = ({
  searchParams,
  schema,
}: {
  searchParams: URLSearchParams;
  schema: z.ZodSchema;
}) => {
  const plainSearchParams = Object.fromEntries(searchParams.entries());
  try {
    const validatedParams = schema.parse(plainSearchParams);
    return new URLSearchParams(validatedParams as AnyObject);
  } catch (error) {
    if (error instanceof z.ZodError) {
      trackError(error.message);
      console.error('Validation error:', error.issues);
    } else {
      trackError(error as string);
      console.error('Unexpected error:', error);
    }
  }
};

export const getLegacyQuery = () => {
  if (typeof window !== 'undefined' && window.location.hash) {
    return window.location.hash
      .replace(/^#/, '')
      .split('&')
      .find((segment) => segment.startsWith('stq='))
      ?.replace('stq=', '');
  }
};

export const useSearchAPI = (
  searchQuery: string,
  itemsPerPage: number,
  searchParams: SearchResultsSchema,
) => {
  const [searchResultsList, setSearchResultsList] =
    useState<QueryResponsePublished>();
  const [queryError, setQueryError] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!searchQuery) {
      setSearchResultsList(undefined);
      setQueryError(null);
      setIsLoading(false);
      return;
    }
    const getAllResults = async () => {
      try {
        setIsLoading(true);
        const results = await getListSearchResultsQuery({
          perPage: itemsPerPage,
          offset: searchParams.page ? searchParams.page * itemsPerPage : 0,
          query: searchQuery,
        });
        setSearchResultsList(results);
        setQueryError(null);
        setIsLoading(false);
      } catch (error) {
        setQueryError(error);
        setIsLoading(false);
      }
    };

    getAllResults();
  }, [searchQuery, itemsPerPage, searchParams.page, searchParams.q]);

  return { list: searchResultsList, queryError, isLoading };
};
